// Módulo Contable v2 — reemplaza Facturacion
import { lazy, Suspense } from "react"
import { Navigate } from "react-router-dom"

/**
 * Wrapper sobre React.lazy que reintenta la carga del chunk y,
 * si sigue fallando, recarga la página para obtener el index.html
 * actualizado con las referencias correctas a los nuevos chunks.
 */
function lazyWithRetry(importFn: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    importFn().catch(() => {
      const key = "chunk_reload_" + importFn.toString().slice(0, 60)
      const alreadyReloaded = sessionStorage.getItem(key)
      if (!alreadyReloaded) {
        sessionStorage.setItem(key, "1")
        window.location.reload()
        return new Promise(() => {}) // nunca resuelve, la página se recarga
      }
      sessionStorage.removeItem(key)
      return importFn() // segundo intento real, si falla el error sube al ErrorBoundary
    })
  )
}

const Login = lazyWithRetry(() => import("../pages/authentication/Login"))
const PagoPublico = lazyWithRetry(() => import("@/pages/PagoPublico"))
const Home = lazyWithRetry(() => import("../pages/Home"))
const Users = lazyWithRetry(() => import("@/pages/Users"))
// Las 5 páginas principales llevan su import() en una función nombrada para
// reusarla en el prefetch on-hover del sidebar (routePrefetchers, abajo).
// Vite deduplica: el chunk es el mismo que carga el lazy de la ruta.
const importClientes = () => import("@/pages/Clientes")
const importDashboard = () => import("@/pages/Dashboard")
const importSales = () => import("@/pages/Sales")
const importMsisdn = () => import("@/pages/Msisdn")
const importPortabilitiesManagement = () => import("@/pages/PortabilitiesManagement")
const Clientes = lazyWithRetry(importClientes)
const Portability = lazyWithRetry(() => import("@/pages/Portability"))
const Dashboard = lazyWithRetry(importDashboard)
const Sales = lazyWithRetry(importSales)
const Batch = lazyWithRetry(() => import("@/pages/Batch"))
const Preactivacion = lazyWithRetry(() => import("@/pages/Preactivacion"))
const Welcome = lazyWithRetry(() => import("@/pages/Welcome"))
const Ofertas = lazyWithRetry(() => import("@/pages/Ofertas"))
const CompanyData = lazyWithRetry(() => import("@/pages/CompanyData"))
const Msisdn = lazyWithRetry(importMsisdn)
const Contabilidad = lazyWithRetry(() => import("@/pages/Contabilidad"))
const Liquidaciones = lazyWithRetry(() => import("@/pages/Liquidaciones"))
const EcommerceOrders = lazyWithRetry(() => import("@/pages/EcommerceOrders"))
const EcommerceProducts = lazyWithRetry(() => import("@/pages/EcommerceProducts"))
const EsimProfiles = lazyWithRetry(() => import("@/pages/EsimProfiles"))
const Empresas = lazyWithRetry(() => import("@/pages/Empresas"))
const EmpresaDetalle = lazyWithRetry(() => import("@/pages/EmpresaDetalle"))
const PromocionesAltan = lazyWithRetry(() => import("@/pages/PromocionesAltan"))
const Promociones = lazyWithRetry(() => import("@/pages/Promociones"))
const PromocionEditor = lazyWithRetry(() => import("@/pages/PromocionEditor"))
const Banners = lazyWithRetry(() => import("@/pages/Banners"))
const SmsCampaigns = lazyWithRetry(() => import("@/pages/SmsCampaigns"))
const PushCampaigns = lazyWithRetry(() => import("@/pages/PushCampaigns"))
const Medallas = lazyWithRetry(() => import("@/pages/Medallas"))
const Familias = lazyWithRetry(() => import("@/pages/Familias"))
const Beneficios = lazyWithRetry(() => import("@/pages/Beneficios"))
// /sms-templates quedó absorbido por el tab Plantillas de /sms-campaigns
// (el tab se llama "templates"); se conserva redirect por los accesos del
// menú lateral, CommandPalette y bookmarks viejos.
const smsTemplatesRedirect = <Navigate to="/sms-campaigns?tab=templates" replace />
const PortabilitiesManagement = lazyWithRetry(importPortabilitiesManagement)
const Leads = lazyWithRetry(() => import("@/pages/Leads"))
const Perfil = lazyWithRetry(() => import("@/pages/Perfil"))

const PageLoader = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Cargando...</span>
    </div>
  </div>
)

const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={<PageLoader />}>
    <Component />
  </Suspense>
)

const authProtectedRoutes = [
  { path: "/", component: withSuspense(Dashboard) },
  { path: "/inicio", component: withSuspense(Welcome) },
  { path: "/usuarios", component: withSuspense(Users) },
  { path: "/clientes", component: withSuspense(Clientes) },
  { path: "/portabilidad", component: withSuspense(Portability) },
  { path: "/consulta-uf", component: withSuspense(Home) },
  { path: "/batch", component: withSuspense(Batch) },
  { path: "/preactivacion", component: withSuspense(Preactivacion) },
  { path: "/ventas", component: withSuspense(Sales) },
  { path: "/menu", component: withSuspense(Home) },
  { path: "/ofertas", component: withSuspense(Ofertas) },
  { path: "/datos-empresa", component: withSuspense(CompanyData) },
  { path: "/msisdn", component: withSuspense(Msisdn) },
  { path: "/facturacion", component: withSuspense(Contabilidad) },
  { path: "/liquidaciones", component: withSuspense(Liquidaciones) },
  { path: "/ecommerce", component: withSuspense(EcommerceOrders) },
  { path: "/ecommerce-products", component: withSuspense(EcommerceProducts) },
  { path: "/esim-profiles", component: withSuspense(EsimProfiles) },
  { path: "/empresas", component: withSuspense(Empresas) },
  { path: "/empresas/:id", component: withSuspense(EmpresaDetalle) },
  { path: "/promociones-altan", component: withSuspense(PromocionesAltan) },
  { path: "/promociones", component: withSuspense(Promociones) },
  { path: "/promociones/nueva", component: withSuspense(PromocionEditor) },
  { path: "/promociones/:id", component: withSuspense(PromocionEditor) },
  { path: "/banners", component: withSuspense(Banners) },
  { path: "/sms-campaigns", component: withSuspense(SmsCampaigns) },
  { path: "/push-campaigns", component: withSuspense(PushCampaigns) },
  { path: "/medallas", component: withSuspense(Medallas) },
  { path: "/familias", component: withSuspense(Familias) },
  { path: "/beneficios", component: withSuspense(Beneficios) },
  { path: "/sms-templates", component: smsTemplatesRedirect },
  { path: "/portabilidades", component: withSuspense(PortabilitiesManagement) },
  { path: "/leads", component: withSuspense(Leads) },
  // Self-service: no requiere entrada de menú (allowlist en AuthProtected)
  { path: "/perfil", component: withSuspense(Perfil) },
]

const publicRoutes = [
  { path: "/login", component: withSuspense(Login) },
  // Página pública de pago de "Nueva Orden" (venta manual de call center, todas las marcas):
  // sin sesión, con el branding del tenant del host. NUNCA marca la orden como pagada — sólo el
  // webhook de la pasarela lo hace del lado del backend.
  { path: "/pagar/:uuid", component: withSuspense(PagoPublico) },
]

/**
 * Prefetch on-hover del sidebar: path → loader del chunk de la página.
 * Limitado a las 5 páginas principales (mapear las ~25 sería frágil: las
 * URLs del menú vienen del API y no todas coinciden 1:1 con una ruta).
 * Un prefetch fallido o de una ruta equivocada es inofensivo: solo calienta
 * un chunk que quizá no se use. El lazy de la ruta reutiliza el mismo chunk.
 */
const routePrefetchers: Record<string, () => Promise<unknown>> = {
  "/": importDashboard,
  "/dashboard": importDashboard,
  "/clientes": importClientes,
  "/msisdn": importMsisdn,
  "/ventas": importSales,
  "/portabilidades": importPortabilitiesManagement,
}

export { authProtectedRoutes, publicRoutes, routePrefetchers }
